import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, Form, InputNumber, Row, Col, Divider, Space, Select, Spin, theme, Tooltip, Input, Button, Typography, Badge } from 'antd';
import { PercentageOutlined, ShoppingCartOutlined, TeamOutlined, UserOutlined, TagOutlined, InfoCircleOutlined, MessageOutlined, CalendarOutlined, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { GET_PRODUCTS } from '../../../mutations/products';
import { SEARCH_CUSTOMERS } from '../../../mutations/customers';
import CustomerDateFilterModal from '../../customers/CustomerDateFilterModal';
import AdvancedFilterModal from '../../customers/AdvancedFilterModal';
import ActiveFilters from '../../customers/ActiveFilters';
import ActiveAdvancedFilters from '../../customers/ActiveAdvancedFilters';
import { useAccount } from '../../../contexts/AccountContext';

const { Text } = Typography;

const SelectionStep = ({
  campaignProcessed,
  campaignMode,
  onSelectionModeChange,
  products,
  customers,
  groups,
  tags,
  productsLoading,
  customersLoading,
  loadingGroups,
  loadingTags,
  handleSearchGroups,
  handleSearchTags,
  customerSelectionMode = 'direct',
  onCustomerSelectionModeChange,
  selectedCampaignType,
  onDateFiltersChange,
  onAdvancedFiltersChange,
  getCampaignCustomersCount
}) => {
  const { token } = theme.useToken();
  const { account } = useAccount();
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [searchedProducts, setSearchedProducts] = useState([]);

  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchedCustomers, setSearchedCustomers] = useState([]);

  // Estado para filtros de data
  const [isDateFilterModalVisible, setIsDateFilterModalVisible] = useState(false);
  const [dateFilters, setDateFilters] = useState([]);
  
  // Estado para filtros avançados
  const [isAdvancedFilterModalVisible, setIsAdvancedFilterModalVisible] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  
  // Estado para contagem de clientes nos filtros de data
  const [dateFiltersCustomersCount, setDateFiltersCustomersCount] = useState(0);
  const [loadingCustomersCount, setLoadingCustomersCount] = useState(false);
  
  // Estado para contagem de clientes nos filtros avançados
  const [advancedFiltersCustomersCount, setAdvancedFiltersCustomersCount] = useState(0);
  const [loadingAdvancedCustomersCount, setLoadingAdvancedCustomersCount] = useState(false);

  // Refs para armazenar os timeouts
  const productTimeoutRef = useRef(null);
  const customerTimeoutRef = useRef(null);

  const [fetchProducts, { loading: searchingProducts }] = useLazyQuery(GET_PRODUCTS, {
    onCompleted: (data) => {
      if (data && data.products && data.products.edges) {
        setSearchedProducts(data.products.edges.map(edge => edge.node));
      }
    }
  });

  const [fetchCustomers, { loading: searchingCustomers }] = useLazyQuery(SEARCH_CUSTOMERS, {
    onCompleted: (data) => {
      if (data && data.searchCustomers) {
        setSearchedCustomers(data.searchCustomers);
      }
    }
  });

  // Hook para debounce de pesquisa de produtos
  const debouncedProductSearch = useCallback(
    (value) => {
      // Limpa o timeout anterior se existir
      if (productTimeoutRef.current) {
        clearTimeout(productTimeoutRef.current);
      }
      
      productTimeoutRef.current = setTimeout(() => {
        if (value && value.length >= 3) {
          fetchProducts({
            variables: {
              search: value,
              perPage: 20
            }
          });
        } else {
          setSearchedProducts([]);
        }
      }, 1000); // 1000ms de delay
    },
    [fetchProducts]
  );

  // Hook para debounce de pesquisa de clientes
  const debouncedCustomerSearch = useCallback(
    (value) => {
      // Limpa o timeout anterior se existir
      if (customerTimeoutRef.current) {
        clearTimeout(customerTimeoutRef.current);
      }
      
      customerTimeoutRef.current = setTimeout(() => {
        if (value && value.length >= 3) {
          fetchCustomers({
            variables: {
              query: value,
              withWhatsapp: true  // Para campanhas, só buscar clientes com WhatsApp
            }
          });
        } else {
          setSearchedCustomers([]);
        }
      }, 1000); // 1000ms de delay
    },
    [fetchCustomers]
  );

  // Função para carregar contagem de clientes baseada nos filtros de data
  const loadDateFiltersCustomersCount = useCallback(async (filters) => {
    if (!filters || filters.length === 0 || !getCampaignCustomersCount) {
      setDateFiltersCustomersCount(0);
      return;
    }

    setLoadingCustomersCount(true);
    
    try {
      // Mapear os filtros para o formato esperado pelo backend (usando camelCase como na página de clientes)
      const mappedFilters = filters.map(filter => ({
        field: filter.field,
        filterType: filter.filterType || filter.type,
        startDate: filter.startDate,
        endDate: filter.endDate,
        month: filter.month,
        day: filter.day,
        year: filter.year
      }));

      const result = await getCampaignCustomersCount({
        variables: {
          kind: selectedCampaignType,
          dateFilters: mappedFilters
        }
      })

      if (result && result.data?.campaignCustomersCount !== undefined) {
        setDateFiltersCustomersCount(result.data.campaignCustomersCount);
      } else {
        console.warn('getCampaignCustomersCount retornou resultado inválido:', result);
        setDateFiltersCustomersCount(0);
      }
    } catch (error) {
      console.error('Erro ao carregar contagem de clientes por filtros de data:', error);
      setDateFiltersCustomersCount(0);
    } finally {
      setLoadingCustomersCount(false);
    }
  }, [getCampaignCustomersCount, selectedCampaignType]);

  // Cleanup dos timeouts quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (productTimeoutRef.current) {
        clearTimeout(productTimeoutRef.current);
      }
      if (customerTimeoutRef.current) {
        clearTimeout(customerTimeoutRef.current);
      }
    };
  }, []);

  // Sincronizar filtros de data com o formulário Antd
  useEffect(() => {
    if (customerSelectionMode === 'date_filters') {
      // Usar uma referência direta ao form seria melhor, mas como não temos acesso direto,
      // vamos simular a validação verificando se existem filtros
      // O form será atualizado quando o usuário aplicar os filtros através do modal
    }
  }, [dateFilters, customerSelectionMode]);

  // Carregar contagem de clientes quando os filtros de data mudarem
  useEffect(() => {
    if (customerSelectionMode === 'date_filters' && dateFilters.length > 0) {
      loadDateFiltersCustomersCount(dateFilters);
    } else {
      setDateFiltersCustomersCount(0);
    }
  }, [dateFilters, customerSelectionMode, loadDateFiltersCustomersCount]);

  // Sincronizar filtros avançados com o formulário Antd
  useEffect(() => {
    if (customerSelectionMode === 'advanced_filters') {
      // Atualizar o campo do formulário com os filtros avançados
      // Isso é necessário para que a validação funcione corretamente
      const form = document.querySelector('form');
      if (form) {
        const event = new CustomEvent('advancedFiltersChange', { 
          detail: { filters: advancedFilters } 
        });
        form.dispatchEvent(event);
      }
    }
  }, [advancedFilters, customerSelectionMode]);

  // Estilo comum para todos os cards para garantir o mesmo tamanho
  const cardStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
  };

  // Estilo para o container do conteúdo dentro do card
  const cardContentStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  };

  // Estilo personalizado para o Divider
  const customDividerStyle = {
    position: 'relative',
    marginBottom: '24px',
    marginTop: '16px',
    borderTopColor: '#f0f0f0'
  };

  // Estilo para o texto do Divider
  const dividerTextStyle = {
    position: 'absolute',
    top: '-14px',
    left: '0',
    padding: '0 8px 0 0',
    backgroundColor: '#fff',
    fontSize: '16px',
    fontWeight: 'bold'
  };

  const handleProductSearch = (value) => {
    setProductSearchTerm(value);
    debouncedProductSearch(value);
  };

  const handleCustomerSearch = (value) => {
    setCustomerSearchTerm(value);
    debouncedCustomerSearch(value);
  };

  // Handlers para seleção de cards
  const handleCampaignModeChange = (mode) => {
    if (campaignProcessed) return;
    onSelectionModeChange({ target: { value: mode } });
  };

  const handleCustomerSelectionModeChange = (mode) => {
    if (campaignProcessed) return;
    onCustomerSelectionModeChange(mode);
  };

  // Funções para controlar o modal de filtros de data
  const showDateFilterModal = () => {
    setIsDateFilterModalVisible(true);
  };

  const handleDateFilterCancel = () => {
    setIsDateFilterModalVisible(false);
  };

  const handleDateFilterApply = (filters) => {
    setDateFilters(filters);
    setIsDateFilterModalVisible(false);
    
    // Notificar o componente pai sobre a mudança nos filtros
    if (onDateFiltersChange) {
      onDateFiltersChange(filters);
    }
  };

  const handleRemoveDateFilter = (filterIndex) => {
    const newFilters = dateFilters.filter((_, index) => index !== filterIndex);
    setDateFilters(newFilters);
    
    // Notificar o componente pai sobre a mudança nos filtros
    if (onDateFiltersChange) {
      onDateFiltersChange(newFilters);
    }
  };

  const handleClearAllDateFilters = () => {
    setDateFilters([]);
    
    // Notificar o componente pai sobre a mudança nos filtros
    if (onDateFiltersChange) {
      onDateFiltersChange([]);
    }
  };

  // Handlers para filtros avançados
  const handleAdvancedFilterApply = async (filters) => {
    setAdvancedFilters(filters);
    setIsAdvancedFilterModalVisible(false);
    
    // Notificar o componente pai sobre a mudança nos filtros
    if (onAdvancedFiltersChange) {
      onAdvancedFiltersChange(filters);
    }
    
    // Carregar contagem real de clientes com filtros avançados
    if (filters.length > 0 && getCampaignCustomersCount) {
      setLoadingAdvancedCustomersCount(true);
      try {
        const result = await getCampaignCustomersCount({
          variables: {
            kind: selectedCampaignType,
            advancedFilters: filters
          }
        });
        
        if (result && result.data?.campaignCustomersCount !== undefined) {
          setAdvancedFiltersCustomersCount(result.data.campaignCustomersCount);
        } else {
          console.warn('getCampaignCustomersCount retornou resultado inválido para filtros avançados:', result);
          setAdvancedFiltersCustomersCount(0);
        }
      } catch (error) {
        console.error('Erro ao carregar contagem de clientes com filtros avançados:', error);
        setAdvancedFiltersCustomersCount(0);
      } finally {
        setLoadingAdvancedCustomersCount(false);
      }
    } else {
      setAdvancedFiltersCustomersCount(0);
    }
  };

  const handleAdvancedFilterCancel = () => {
    setIsAdvancedFilterModalVisible(false);
  };

  const handleRemoveAdvancedFilter = (filterIndex) => {
    const newFilters = advancedFilters.filter((_, index) => index !== filterIndex);
    setAdvancedFilters(newFilters);
    setAdvancedFiltersCustomersCount(newFilters.length > 0 ? 25 : 0);
  };

  const handleClearAllAdvancedFilters = () => {
    setAdvancedFilters([]);
    setAdvancedFiltersCustomersCount(0);
  };

  const getActiveFiltersCount = () => {
    return dateFilters.length;
  };

  const getActiveAdvancedFiltersCount = () => {
    return advancedFilters.length;
  };

  // Determine which products to display in the Select component
  const productsToDisplay = productSearchTerm && productSearchTerm.length >= 3
    ? searchedProducts
    : [];

  // Determine which customers to display in the Select component
  const customersToDisplay = customerSearchTerm && customerSearchTerm.length >= 3
    ? searchedCustomers
    : [];

  return (
    <Card>
      <Spin spinning={productsLoading || customersLoading}>
        {/* Objetivo do Follow Up - apenas para campanhas de follow_up */}
        {selectedCampaignType === 'follow_up' && (
          <>
            <div style={customDividerStyle}>
              <div style={dividerTextStyle}>
                <Space>
                  <MessageOutlined />
                  Objetivo do Follow Up
                </Space>
              </div>
              <Divider style={{ margin: 0 }} />
            </div>

            <Form.Item
              name="followUpObjective"
              label="Descreva o objetivo deste follow-up"
              rules={[{ required: true, message: 'Por favor, digite o objetivo do follow up' }]}
            >
              <Input.TextArea 
                placeholder="Ex: Verificar satisfação com o produto comprado, solicitar feedback sobre o atendimento, acompanhar instalação, etc."
                rows={3}
                disabled={campaignProcessed}
              />
            </Form.Item>
          </>
        )}

        {/* Percentual de desconto - apenas para campanhas de oferta */}
        {selectedCampaignType === 'offer' && (
          <>
            <div style={customDividerStyle}>
              <div style={dividerTextStyle}>
                <Space>
                  <PercentageOutlined />
                  Percentual de Desconto
                </Space>
              </div>
              <Divider style={{ margin: 0 }} />
            </div>

            <Form.Item
              name="discountPercentage"
              rules={[
                { type: 'number', min: 0, max: 100, message: 'O percentual deve estar entre 0 e 100' }
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                placeholder="Ex: 10"
                disabled={campaignProcessed}
                style={{ width: '80px' }}
                formatter={value => `${value}%`}
                parser={value => value.replace('%', '')}
              />
            </Form.Item>
          </>
        )}

        {/* Seleção de produtos - apenas para campanhas de oferta */}
        {selectedCampaignType === 'offer' && (
          <>
            <div style={customDividerStyle}>
              <div style={dividerTextStyle}>
                <Space>
                  <ShoppingCartOutlined />
                  <span>Modo de Seleção de Produtos</span>
                </Space>
              </div>
              <Divider style={{ margin: 0 }} />
            </div>

            <Form.Item>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card
                    hoverable
                    style={{
                      ...cardStyle,
                      borderColor: campaignMode === 'products' ? token.colorPrimary : undefined,
                      backgroundColor: campaignMode === 'products' ? `${token.colorPrimary}10` : undefined
                    }}
                    bodyStyle={{ height: '100%', padding: '16px' }}
                    onClick={() => handleCampaignModeChange('products')}
                  >
                    <div style={cardContentStyle}>
                      <div>
                        <Space>
                          <ShoppingCartOutlined style={{ color: campaignMode === 'products' ? token.colorPrimary : '#ff4d4f' }} />
                          <span><strong>Produtos Individuais</strong></span>
                        </Space>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        Selecione produtos específicos para a oferta
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    hoverable
                    style={{
                      ...cardStyle,
                      borderColor: campaignMode === 'group' ? token.colorPrimary : undefined,
                      backgroundColor: campaignMode === 'group' ? `${token.colorPrimary}10` : undefined
                    }}
                    bodyStyle={{ height: '100%', padding: '16px' }}
                    onClick={() => handleCampaignModeChange('group')}
                  >
                    <div style={cardContentStyle}>
                      <div>
                        <Space>
                          <TeamOutlined style={{ color: campaignMode === 'group' ? token.colorPrimary : '#1890ff' }} />
                          <span><strong>Grupo de Produtos</strong></span>
                        </Space>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        Selecione um grupo completo de produtos
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Form.Item>

            {/* Campo oculto para armazenar o valor no formulário */}
            <Form.Item name="campaignMode" hidden initialValue="products">
              <input type="hidden" />
            </Form.Item>

            {campaignMode === 'products' ? (
              <Form.Item
                name="selectedProducts"
                label="Selecione os Produtos para Oferta"
                rules={[{ required: true, message: 'Por favor, selecione pelo menos um produto' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Digite 3 ou mais caracteres para pesquisar produtos"
                  style={{ width: '100%' }}
                  optionFilterProp="label"
                  loading={searchingProducts}
                  showSearch
                  onSearch={handleProductSearch}
                  filterOption={false}
                  disabled={campaignProcessed}
                  notFoundContent={
                    searchingProducts ? <Spin size="small" /> :
                      productSearchTerm && productSearchTerm.length >= 3
                        ? "Nenhum produto encontrado"
                        : "Digite 3 ou mais caracteres para pesquisar"
                  }
                >
                  {productsToDisplay.map(product => (
                    <Select.Option key={product.id} value={product.id} label={product.name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{product.name}</span><span> </span>
                        {product.reference && <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>Cód: {product.reference}</span>}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <Form.Item
                name="selectedGroup"
                label="Selecione um Grupo de Produtos"
                rules={[{ required: true, message: 'Por favor, selecione um grupo de produtos' }]}
              >
                <Select
                  placeholder="Selecione um grupo de produtos"
                  style={{ width: '100%' }}
                  optionFilterProp="label"
                  loading={loadingGroups}
                  showSearch
                  onSearch={handleSearchGroups}
                  filterOption={false}
                  disabled={campaignProcessed}
                  notFoundContent={loadingGroups ? <Spin size="small" /> : "Nenhum grupo encontrado"}
                >
                  {groups.map(group => (
                    <Select.Option key={group.id} value={group.id} label={group.name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{group.name}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </>
        )}

        <div style={customDividerStyle}>
          <div style={dividerTextStyle}>
            <Space>
              <UserOutlined />
              <span>Modo de Seleção de Clientes</span>
              <Tooltip 
                title={
                  <div>
                    <div>ℹ️ <strong>Informação importante:</strong></div>
                    <br />
                    <div>• A pesquisa de clientes mostra apenas clientes com WhatsApp preenchido</div>
                    <div>• As tags exibem apenas a contagem de clientes que possuem WhatsApp</div>
                    {account?.onlyVerifiedWhatsappCampaigns && (
                      <div>• <strong>Apenas clientes com WhatsApp validado</strong> serão incluídos nas campanhas</div>
                    )}
                    <br />
                    <div>Isso garante que apenas clientes que podem receber mensagens sejam selecionados para a campanha.</div>
                  </div>
                }
                placement="top"
                overlayStyle={{ maxWidth: '400px' }}
              >
                <InfoCircleOutlined 
                  style={{ 
                    color: '#1890ff', 
                    fontSize: '14px',
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            </Space>
          </div>
          <Divider style={{ margin: 0 }} />
        </div>

        <Form.Item>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Card
                hoverable
                style={{
                  ...cardStyle,
                  borderColor: customerSelectionMode === 'direct' ? token.colorPrimary : undefined,
                  backgroundColor: customerSelectionMode === 'direct' ? `${token.colorPrimary}10` : undefined
                }}
                bodyStyle={{ height: '100%', padding: '16px' }}
                onClick={() => handleCustomerSelectionModeChange('direct')}
              >
                <div style={cardContentStyle}>
                  <div>
                    <Space>
                      <UserOutlined style={{ color: customerSelectionMode === 'direct' ? token.colorPrimary : '#1890ff' }} />
                      <span><strong>Clientes Diretos</strong></span>
                    </Space>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Selecione clientes específicos
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card
                hoverable
                style={{
                  ...cardStyle,
                  borderColor: customerSelectionMode === 'tags' ? token.colorPrimary : undefined,
                  backgroundColor: customerSelectionMode === 'tags' ? `${token.colorPrimary}10` : undefined
                }}
                bodyStyle={{ height: '100%', padding: '16px' }}
                onClick={() => handleCustomerSelectionModeChange('tags')}
              >
                <div style={cardContentStyle}>
                  <div>
                    <Space>
                      <TagOutlined style={{ color: customerSelectionMode === 'tags' ? token.colorPrimary : '#52c41a' }} />
                      <span><strong>Clientes por Tag</strong></span>
                    </Space>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Selecione tags para identificar clientes
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card
                hoverable
                style={{
                  ...cardStyle,
                  borderColor: customerSelectionMode === 'date_filters' ? token.colorPrimary : undefined,
                  backgroundColor: customerSelectionMode === 'date_filters' ? `${token.colorPrimary}10` : undefined
                }}
                bodyStyle={{ height: '100%', padding: '16px' }}
                onClick={() => handleCustomerSelectionModeChange('date_filters')}
              >
                <div style={cardContentStyle}>
                  <div>
                    <Space>
                      <CalendarOutlined style={{ color: customerSelectionMode === 'date_filters' ? token.colorPrimary : '#ff7a00' }} />
                      <span><strong>Clientes por Data</strong></span>
                    </Space>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Filtre clientes por período
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card
                hoverable
                style={{
                  ...cardStyle,
                  borderColor: customerSelectionMode === 'advanced_filters' ? token.colorPrimary : undefined,
                  backgroundColor: customerSelectionMode === 'advanced_filters' ? `${token.colorPrimary}10` : undefined
                }}
                bodyStyle={{ height: '100%', padding: '16px' }}
                onClick={() => handleCustomerSelectionModeChange('advanced_filters')}
              >
                <div style={cardContentStyle}>
                  <div>
                    <Space>
                      <FilterOutlined style={{ color: customerSelectionMode === 'advanced_filters' ? token.colorPrimary : '#722ed1' }} />
                      <span><strong>Seleção Avançada</strong></span>
                    </Space>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Filtros avançados de clientes
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Form.Item>

        {/* Campo oculto para armazenar o valor no formulário */}
        <Form.Item name="customerSelectionMode" hidden initialValue="direct">
          <input type="hidden" />
        </Form.Item>

        {customerSelectionMode === 'direct' ? (
          <Form.Item
            name="selectedCustomers"
            label="Selecione os Clientes para Receber a Oferta"
            rules={[{ required: customerSelectionMode === 'direct', message: 'Por favor, selecione pelo menos um cliente' }]}
          >
            <Select
              mode="multiple"
              placeholder="Digite 3 ou mais caracteres para pesquisar clientes"
              style={{ width: '100%' }}
              optionFilterProp="label"
              loading={searchingCustomers}
              showSearch
              onSearch={handleCustomerSearch}
              filterOption={false}
              disabled={campaignProcessed}
              notFoundContent={
                searchingCustomers ? <Spin size="small" /> :
                  customerSearchTerm && customerSearchTerm.length >= 3
                    ? "Nenhum cliente encontrado"
                    : "Digite 3 ou mais caracteres para pesquisar"
              }
            >
              {customersToDisplay.map(customer => (
                <Select.Option key={customer.id} value={customer.id} label={customer.name}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{customer.name}</span> <span> </span> 
                    <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{customer.whatsapp}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : customerSelectionMode === 'tags' ? (
          <Form.Item
            name="selectedTags"
            label="Selecione as Tags para Identificar Clientes"
            rules={[{ required: customerSelectionMode === 'tags', message: 'Por favor, selecione pelo menos uma tag' }]}
          >
            <Select
              mode="multiple"
              placeholder="Selecione tags para identificar clientes"
              style={{ width: '100%' }}
              optionFilterProp="label"
              loading={loadingTags}
              showSearch
              onSearch={handleSearchTags}
              filterOption={false}
              disabled={campaignProcessed}
              notFoundContent={loadingTags ? <Spin size="small" /> : "Nenhuma tag encontrada"}
            >
              {tags?.filter(tag => (tag.customersWithWhatsappCount || tag.customersCount) > 0).map(tag => {
                // Para campanhas, mostrar apenas clientes com WhatsApp
                const clientCount = tag.customersWithWhatsappCount || tag.customersCount;
                return (
                  <Select.Option key={tag.id} value={tag.id} label={tag.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{tag.name}</span>
                      <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}> - {clientCount} cliente{clientCount !== 1 ? 's' : ''}</span>
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        ) : customerSelectionMode === 'date_filters' ? (
          <div>
            <Form.Item
              name="selectedDateFilters"
              label="Filtros de Data Aplicados"
              rules={[{ required: customerSelectionMode === 'date_filters', message: 'Por favor, aplique pelo menos um filtro de data' }]}
            >
              <div>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Button
                    type="primary"
                    icon={<FilterOutlined />}
                    onClick={showDateFilterModal}
                    disabled={campaignProcessed}
                  >
                    {dateFilters.length > 0 ? `Editar Filtros (${getActiveFiltersCount()})` : 'Adicionar Filtros de Data'}
                  </Button>
                  
                  {dateFilters.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {loadingCustomersCount ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Spin size="small" />
                          <Text type="secondary">Contando clientes...</Text>
                        </div>
                      ) : (
                        <Badge 
                          count={dateFiltersCustomersCount} 
                          style={{ backgroundColor: '#1890ff' }}
                          showZero
                        />
                      )}
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {dateFiltersCustomersCount === 1 ? 'cliente selecionado' : 'clientes selecionados'}
                      </Text>
                    </div>
                  )}
                </div>
                
                {dateFilters.length > 0 && (
                  <ActiveFilters
                    filters={dateFilters}
                    onRemoveFilter={handleRemoveDateFilter}
                    onClearAll={handleClearAllDateFilters}
                    disabled={campaignProcessed}
                  />
                )}
              </div>
            </Form.Item>
          </div>
        ) : customerSelectionMode === 'advanced_filters' ? (
          <div>
            <Form.Item
              name="selectedAdvancedFilters"
              label="Filtros Avançados Aplicados"
              rules={[{ 
                required: customerSelectionMode === 'advanced_filters', 
                message: 'Por favor, aplique pelo menos um filtro avançado',
                validator: (_, value) => {
                  if (customerSelectionMode === 'advanced_filters' && (!advancedFilters || advancedFilters.length === 0)) {
                    return Promise.reject(new Error('Por favor, aplique pelo menos um filtro avançado'));
                  }
                  return Promise.resolve();
                }
              }]}
              initialValue={advancedFilters}
            >
              <div>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Button
                    type="primary"
                    icon={<FilterOutlined />}
                    onClick={() => setIsAdvancedFilterModalVisible(true)}
                    disabled={campaignProcessed}
                  >
                    {advancedFilters.length > 0 ? `Editar Filtros (${getActiveAdvancedFiltersCount()})` : 'Adicionar Filtros Avançados'}
                  </Button>
                  
                  {advancedFilters.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {loadingAdvancedCustomersCount ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Spin size="small" />
                          <Text type="secondary">Contando clientes...</Text>
                        </div>
                      ) : (
                        <Badge 
                          count={advancedFiltersCustomersCount} 
                          style={{ backgroundColor: '#722ed1' }}
                          showZero
                        />
                      )}
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {advancedFiltersCustomersCount === 1 ? 'cliente selecionado' : 'clientes selecionados'}
                      </Text>
                    </div>
                  )}
                </div>
                
                {advancedFilters.length > 0 && (
                  <ActiveAdvancedFilters
                    filters={advancedFilters}
                    onRemoveFilter={handleRemoveAdvancedFilter}
                    onClearAll={handleClearAllAdvancedFilters}
                  />
                )}
              </div>
            </Form.Item>
          </div>
        ) : null}

      </Spin>

      {/* Modal de filtros de data */}
      <CustomerDateFilterModal
        open={isDateFilterModalVisible}
        onCancel={handleDateFilterCancel}
        onApply={handleDateFilterApply}
        initialFilters={dateFilters}
      />

      {/* Modal de filtros avançados */}
      <AdvancedFilterModal
        open={isAdvancedFilterModalVisible}
        onCancel={handleAdvancedFilterCancel}
        onApply={handleAdvancedFilterApply}
        initialFilters={advancedFilters}
      />
    </Card>
  );
};

export default SelectionStep;